// =====================================================================
// Vider la file d'attente
// =====================================================================
// On repasse devant une antenne : tout ce qui a été saisi au village
// part, dans l'ordre où il a été saisi.
//
// L'ORDRE N'EST PAS UN DÉTAIL. Un grand-père déclaré avant le parent par
// lequel on y est rattaché ferait créer au serveur un nœud anonyme
// intermédiaire, et le parent déclaré ensuite viendrait à côté au lieu de
// s'y substituer. On envoie donc une entrée à la fois, en séquence — pas
// en parallèle, même si c'est plus lent.
//
// DEUX SORTES D'ÉCHEC, deux conduites :
//
//   - le réseau retombe → on s'arrête net et on garde tout le reste. Rien
//     ne sert d'essayer les quatorze suivantes pour les voir échouer.
//   - le serveur refuse → l'entrée est marquée et la file continue. Un
//     père déjà déclaré ne doit pas bloquer les treize autres personnes.
// =====================================================================

import { envoyerDeclaration, envoyerRecit, ErreurApi } from './api';
import {
  lister,
  listerRecits,
  marquerEchec,
  retirer,
  RECITS,
  type Declaration,
} from './file-attente';

export interface Bilan {
  envoyees: number;
  refusees: number;
  /** Les récits sont comptés à part : ce ne sont pas les mêmes volumes,
   *  et l'utilisateur ne les attend pas de la même façon. */
  recits: number;
  /** Vrai quand on s'est arrêté faute de réseau : il reste du travail. */
  interrompu: boolean;
}

/** Le transport, injectable pour pouvoir vérifier la file sans réseau. */
export type Envoi = (
  d: Declaration,
  cle: string,
) => Promise<{ personne_id: string }>;

let enCours: Promise<Bilan> | null = null;

export function synchroniser(envoi: Envoi = envoyerDeclaration): Promise<Bilan> {
  // Un seul passage à la fois : le retour du réseau et l'ouverture de
  // l'application se déclenchent souvent dans la même seconde, et deux
  // passages concurrents enverraient chaque entrée deux fois.
  if (enCours) return enCours;

  enCours = vider(envoi).finally(() => {
    enCours = null;
  });
  return enCours;
}

async function vider(envoi: Envoi): Promise<Bilan> {
  const bilan: Bilan = {
    envoyees: 0,
    refusees: 0,
    recits: 0,
    interrompu: false,
  };
  const file = await lister();

  for (const entree of file) {
    // Une entrée déjà refusée reste dans la file pour être vue, mais on
    // ne la repropose pas à chaque passage : c'est à l'utilisateur de la
    // corriger ou de l'abandonner.
    if (entree.erreur) continue;

    try {
      await envoi(entree.declaration, entree.cle);
      await retirer(entree.cle);
      bilan.envoyees++;
    } catch (e) {
      if (e instanceof ErreurApi && (e.statut === 0 || e.statut >= 500)) {
        bilan.interrompu = true;
        break;
      }
      await marquerEchec(
        entree.cle,
        e instanceof ErreurApi ? e.message : 'Envoi impossible.',
      );
      bilan.refusees++;
    }
  }

  // Les récits passent APRÈS les déclarations, et seulement si elles sont
  // toutes passées : un récit se rattache à une fiche, et la fiche doit
  // exister. Deux méga-octets ne doivent pas non plus retenir quatorze
  // déclarations de deux cents octets.
  if (!bilan.interrompu) await viderRecits(bilan);

  return bilan;
}

async function viderRecits(bilan: Bilan): Promise<void> {
  for (const r of await listerRecits()) {
    if (r.erreur) continue;

    try {
      await envoyerRecit({
        personne_id: r.personne_id,
        son: r.son,
        duree_s: r.duree_s,
        titre: r.titre,
      });
      await retirer(r.cle, RECITS);
      bilan.recits++;
    } catch (e) {
      if (e instanceof ErreurApi && (e.statut === 0 || e.statut >= 500)) {
        bilan.interrompu = true;
        return;
      }
      await marquerEchec(
        r.cle,
        e instanceof ErreurApi ? e.message : 'Envoi impossible.',
        RECITS,
      );
      bilan.refusees++;
    }
  }
}

/**
 * Branche la synchronisation sur le retour du réseau, et la lance une
 * fois au démarrage. Renvoie de quoi se débrancher.
 */
export function brancherSynchro(apres: (b: Bilan) => void): () => void {
  const passer = () => {
    if (!navigator.onLine) return;
    void synchroniser().then((b) => {
      if (b.envoyees > 0 || b.refusees > 0) apres(b);
    });
  };

  window.addEventListener('online', passer);
  passer();
  return () => window.removeEventListener('online', passer);
}
